import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ArrowUpRight,
  Palette,
  Code2,
  Server,
  Smartphone,
  Sparkles,
  Cloud,
  Wrench,
  Wand2,
  X,
  Check,
  Clock,
  Tag,
  ArrowRight,
} from 'lucide-react';
import { Section, Container, SectionHead } from './Section';
import { services } from '../data/portfolio';

const iconMap = { Palette, Code2, Server, Smartphone, Sparkles, Cloud, Wrench, Wand2 };

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Card = styled(motion.button)`
  position: relative;
  padding: 26px 22px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};
  overflow: hidden;
  transition: transform 300ms ease, background 300ms ease, border-color 300ms ease;
  cursor: pointer;
  text-align: left;
  color: inherit;
  font: inherit;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    top: -60%;
    right: -60%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(closest-side, rgba(124,92,255,0.25), transparent 70%);
    opacity: 0;
    transition: opacity 300ms ease;
  }

  &:hover {
    transform: translateY(-4px);
    border-color: ${({ theme }) => theme.colors.primarySoft};
    background: ${({ theme }) => theme.colors.surfaceHi};
  }
  &:hover::after { opacity: 1; }

  .icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(124,92,255,0.15);
    color: ${({ theme }) => theme.colors.primarySoft};
    margin-bottom: 18px;
    border: 1px solid rgba(124,92,255,0.25);
  }

  h3 { font-size: 18px; margin-bottom: 8px; }
  .desc {
    color: ${({ theme }) => theme.colors.textDim};
    font-size: 14px;
    line-height: 1.7;
    margin-bottom: 16px;
  }

  .list {
    display: grid;
    gap: 6px;
    margin-bottom: 18px;
  }
  .list span {
    color: ${({ theme }) => theme.colors.textDim};
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    gap: 8px;

    &::before {
      content: '';
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: ${({ theme }) => theme.colors.primarySoft};
    }
  }

  .cta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${({ theme }) => theme.colors.primarySoft};
    font-size: 13px;
    font-weight: 500;
    transition: gap 200ms ease;
  }

  &:hover .cta { gap: 10px; }
`;

/* ----------- Modal ----------- */
const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 80;
  background: ${({ theme }) => theme.colors.modalBackdrop};
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Modal = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.xl};
  padding: 32px;
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 24px;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceHi};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 200ms ease, border-color 200ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.danger};
    border-color: ${({ theme }) => theme.colors.danger};
  }
`;

const Head = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 8px;
  padding-right: 40px;

  .icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(124,92,255,0.15);
    color: ${({ theme }) => theme.colors.primarySoft};
    border: 1px solid rgba(124,92,255,0.25);
    flex-shrink: 0;
  }

  h3 {
    font-size: 24px;
    margin-bottom: 4px;
  }

  .kicker {
    font-family: ${({ theme }) => theme.fonts.mono};
    color: ${({ theme }) => theme.colors.primarySoft};
    font-size: 12px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }
`;

const Desc = styled.p`
  color: ${({ theme }) => theme.colors.textDim};
  font-size: 15px;
  line-height: 1.75;
  margin: 18px 0 22px;
`;

const SubHead = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 20px 0 12px;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    color: ${({ theme }) => theme.colors.text};
    font-size: 14px;
  }

  .tick {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(52, 211, 153, 0.15);
    color: ${({ theme }) => theme.colors.success};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const MetaRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 22px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const MetaBox = styled.div`
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.surfaceHi};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  display: flex;
  align-items: center;
  gap: 12px;

  .icon {
    color: ${({ theme }) => theme.colors.primarySoft};
  }

  .label {
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${({ theme }) => theme.colors.textMuted};
  }

  .value {
    font-size: 15px;
    color: ${({ theme }) => theme.colors.text};
    font-weight: 500;
  }
`;

const ModalActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 26px;
  padding-top: 22px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const PrimaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: transform 200ms ease, background 200ms ease;

  &:hover { transform: translateY(-2px); background: #6947f5; }
`;

const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 999px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  cursor: pointer;
  transition: transform 200ms ease, border-color 200ms ease, background 200ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.primarySoft};
    background: ${({ theme }) => theme.colors.surfaceHi};
  }
`;

export default function Services() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const [selected, setSelected] = useState(null);

  const openModal = (s) => setSelected(s);
  const closeModal = () => setSelected(null);

  const scrollToContact = () => {
    closeModal();
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 200);
  };

  const Icon = selected ? iconMap[selected.icon] || Sparkles : null;

  return (
    <Section id="services" ref={ref}>
      <Container>
        <SectionHead kicker="Services" title="What I" gradient="Offer" sub="End to end services to design, build, launch and maintain modern products." />
        <Grid>
          {services.map((s, i) => {
            const I = iconMap[s.icon] || Sparkles;
            return (
              <Card
                key={s.title}
                onClick={() => openModal(s)}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.06, duration: 0.55 }}
              >
                <span className="icon"><I size={22} /></span>
                <h3>{s.title}</h3>
                <p className="desc">{s.description}</p>
                <div className="list">
                  {s.features.map((f) => (<span key={f}>{f}</span>))}
                </div>
                <div className="cta">See details <ArrowUpRight size={14} /></div>
              </Card>
            );
          })}
        </Grid>
      </Container>

      <AnimatePresence>
        {selected && (
          <Backdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <Modal
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn onClick={closeModal} aria-label="Close"><X size={16} /></CloseBtn>
              <Head>
                <span className="icon"><Icon size={24} /></span>
                <div>
                  <div className="kicker">Service</div>
                  <h3>{selected.title}</h3>
                </div>
              </Head>
              <Desc>{selected.description}</Desc>

              <SubHead>What's Included</SubHead>
              <List>
                {selected.features.map((f) => (
                  <li key={f}><span className="tick"><Check size={12} /></span>{f}</li>
                ))}
              </List>

              <SubHead>Deliverables</SubHead>
              <List>
                {selected.deliverables?.map((d) => (
                  <li key={d}><span className="tick"><Check size={12} /></span>{d}</li>
                ))}
              </List>

              <MetaRow>
                <MetaBox>
                  <span className="icon"><Clock size={18} /></span>
                  <div>
                    <div className="label">Timeline</div>
                    <div className="value">{selected.timeline}</div>
                  </div>
                </MetaBox>
                <MetaBox>
                  <span className="icon"><Tag size={18} /></span>
                  <div>
                    <div className="label">Starting at</div>
                    <div className="value">{selected.startingPrice}</div>
                  </div>
                </MetaBox>
              </MetaRow>

              <ModalActions>
                <PrimaryBtn onClick={scrollToContact}>
                  Start a project <ArrowRight size={16} />
                </PrimaryBtn>
                <GhostBtn onClick={closeModal}>Close</GhostBtn>
              </ModalActions>
            </Modal>
          </Backdrop>
        )}
      </AnimatePresence>
    </Section>
  );
}
